import { useState, useEffect, useCallback } from 'react'
import { api } from '../api'
import { useApp } from '../AppContext'
import { FormGroup, Empty, Loading } from '../components/ui'

export default function Equipe() {
  const { toast }             = useApp()
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal]     = useState(null)  // null | {} | {id,...}

  const load = useCallback(() => {
    setLoading(true)
    api.get('/equipe')
      .then(r => setItems(r.data || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function toggle(id, actif) {
    if (!confirm(`${actif ? 'Désactiver' : 'Réactiver'} ce technicien ?`)) return
    try { await api.delete(`/equipe/${id}`); toast('Statut modifié', 'success'); load() }
    catch (e) { toast(e.message, 'error') }
  }

  const admins = items.filter(u => u.role === 'admin')
  const techs  = items.filter(u => u.role === 'technicien')

  return (
    <div className="content">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn primary" onClick={() => setModal({})}>
          <i className="ti ti-plus" /> Nouveau technicien
        </button>
      </div>

      {/* Admins */}
      {admins.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">
            <span><i className="ti ti-shield" style={{ marginRight: 6, color: 'var(--primary)' }} />Administrateurs</span>
          </div>
          {admins.map(u => <UserRow key={u.id} user={u} onEdit={setModal} onToggle={toggle} />)}
        </div>
      )}

      {/* Techniciens */}
      <div className="card" style={{ padding: 0 }}>
        <div className="card-title" style={{ padding: '14px 18px 0' }}>
          <span><i className="ti ti-tool" style={{ marginRight: 6 }} />Techniciens ({techs.length})</span>
        </div>
        {loading ? <Loading /> : techs.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Nom</th>
                <th style={th}>Email</th>
                <th style={th}>Statut</th>
                <th style={th}>Créé le</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {techs.map(u => <UserRow key={u.id} user={u} onEdit={setModal} onToggle={toggle} tableMode />)}
            </tbody>
          </table>
        ) : <Empty icon="users" message="Aucun technicien — ajoutez-en un !" />}
      </div>

      {modal !== null && (
        <TechModal
          tech={modal}
          onClose={() => setModal(null)}
          onSaved={() => { load(); setModal(null); toast('Enregistré', 'success') }}
        />
      )}
    </div>
  )
}

const th = { textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: '.05em', textTransform: 'uppercase', borderBottom: '2px solid var(--border)', background: 'var(--bg2)' }

function UserRow({ user: u, onEdit, onToggle, tableMode }) {
  const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—'

  const content = (
    <>
      <td style={{ padding: '11px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: u.role === 'admin' ? 'var(--primary-light)' : '#f3e5f5', color: u.role === 'admin' ? 'var(--primary)' : '#6a1b9a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
            {(u.prenom?.[0] || '') + (u.nom?.[0] || '')}
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{u.prenom} {u.nom}</div>
            <div style={{ fontSize: 11, color: u.role === 'admin' ? 'var(--primary)' : '#6a1b9a', fontWeight: 600 }}>
              {u.role === 'admin' ? 'Administrateur' : 'Technicien'}
            </div>
          </div>
        </div>
      </td>
      <td style={{ padding: '11px 14px' }}>{u.email}</td>
      <td style={{ padding: '11px 14px' }}>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: u.actif ? '#e8f5e9' : '#fce4ec', color: u.actif ? '#2e7d32' : '#c62828' }}>
          {u.actif ? '● Actif' : '○ Inactif'}
        </span>
      </td>
      <td style={{ padding: '11px 14px', color: 'var(--text3)', fontSize: 12 }}>{fmtDate(u.created_at)}</td>
      <td style={{ padding: '11px 14px' }}>
        {u.role !== 'admin' && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn sm" onClick={() => onEdit(u)}><i className="ti ti-edit" /></button>
            <button className="btn sm" onClick={() => onToggle(u.id, u.actif)}>
              <i className={`ti ti-${u.actif ? 'user-off' : 'user-check'}`} style={{ color: u.actif ? 'var(--danger)' : 'var(--success)' }} />
            </button>
          </div>
        )}
      </td>
    </>
  )

  return tableMode ? <tr style={{ borderBottom: '1px solid var(--border)' }}>{content}</tr> : (
    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      {u.prenom} {u.nom} — {u.email}
    </div>
  )
}

function TechModal({ tech, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', password: '', ...(tech || {}) })
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      if (tech?.id) await api.put(`/equipe/${tech.id}`, form)
      else          await api.post('/equipe', form)
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <span className="modal-title">{tech?.id ? 'Modifier le technicien' : 'Nouveau technicien'}</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="grid2">
              <FormGroup label="Prénom" required>
                <input className="form-control" value={form.prenom || ''} onChange={f('prenom')} required />
              </FormGroup>
              <FormGroup label="Nom" required>
                <input className="form-control" value={form.nom || ''} onChange={f('nom')} required />
              </FormGroup>
            </div>
            <FormGroup label="Email" required>
              <input type="email" className="form-control" value={form.email || ''} onChange={f('email')} required disabled={!!tech?.id} />
            </FormGroup>
            <FormGroup label={tech?.id ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'} required={!tech?.id}>
              <input type="password" className="form-control" value={form.password || ''} onChange={f('password')} required={!tech?.id} minLength={8} placeholder="8 caractères minimum" />
            </FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? '…' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}